/**
 * Loads a provider's script once and hands back the global it installs.
 *
 * Every provider integration needs this and they all need it identically, so it
 * lives here rather than five times over. Concurrent callers share one load;
 * a failed load is not cached, so a retry can genuinely retry.
 */
const pending = new Map();

export function loadScript(src, { globalName, timeoutMs = 15_000 } = {}) {
  const existingGlobal = globalName ? window[globalName] : undefined;
  if (existingGlobal) return Promise.resolve(existingGlobal);

  if (pending.has(src)) return pending.get(src);

  const promise = new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      pending.delete(src);
      reject(new Error('The secure payment library took too long to load. Please check your connection and try again.'));
    }, timeoutMs);

    const done = (err) => {
      clearTimeout(timer);
      if (err) {
        // Not cached: a network blip should not poison every later attempt.
        pending.delete(src);
        reject(err);
        return;
      }
      const installed = globalName ? window[globalName] : true;
      if (globalName && !installed) {
        pending.delete(src);
        reject(new Error('The secure payment library did not load correctly. Please refresh and try again.'));
        return;
      }
      resolve(installed);
    };

    const existing = document.querySelector(`script[data-payment-lib="${CSS.escape(src)}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') { done(); return; }
      existing.addEventListener('load', () => done());
      existing.addEventListener('error', () => done(new Error('Could not load the secure payment library.')));
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.paymentLib = src;
    script.addEventListener('load', () => { script.dataset.loaded = 'true'; done(); });
    script.addEventListener('error', () => done(new Error('Could not load the secure payment library. Check your connection and try again.')));
    document.head.appendChild(script);
  });

  pending.set(src, promise);
  return promise;
}

/**
 * A provider's own error text is written for developers and frequently names the
 * provider's library — which must never reach a customer on a white-label
 * checkout. Every integration routes its unmapped errors through here.
 */
export function customerSafeError(code, providerText, { contactMerchant = false } = {}) {
  console.error(`[checkout] ${code ?? 'error'} — ${providerText ?? '(no detail)'}`);
  return new Error(
    contactMerchant
      ? 'This payment could not be processed right now. Please contact the merchant — you have not been charged.'
      : 'We could not process that payment. Please check your details and try again.',
  );
}
